import React, { useCallback, useEffect, useState } from "react";
import { Search, AlertTriangle, Clock } from "lucide-react";
import { disputesApi, Stars, SourceBadge, DisputeBadge, fmtDate, inputCls, SOURCE_META } from "./shared";
import DisputePanel from "./DisputePanel";

const FILTERS = [["open", "Açık itirazlar"], ["new", "İtiraz edilmemiş kötü yorumlar"], ["pending", "Cevap bekliyor"], ["overdue", "Gecikmiş (7+ gün)"],
  ["rejected", "Reddedildi → tekrar"], ["maybe_removed", "Kaldırılmış olabilir"], ["removed", "Kaldırıldı"], ["all", "Tüm itirazlar"]];

function Kpi({ label, value, cls = "", onClick, testId }) {
  return (
    <button onClick={onClick} className="text-left bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 hover:border-slate-500 transition-colors" data-testid={testId}>
      <div className={`text-2xl font-bold font-mono ${cls}`}>{value ?? "—"}</div>
      <div className="text-xs text-slate-400">{label}</div>
    </button>
  );
}

function Row({ r, active, onClick }) {
  return (
    <button onClick={onClick} data-testid={`dispute-row-${r.id}`}
      className={`w-full text-left rounded-xl border p-3 transition-colors ${active ? "border-blue-500 ring-2 ring-blue-500/30" : "border-slate-700 hover:border-slate-500"} bg-slate-800`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0"><SourceBadge source={r.source} /><b className="text-white truncate">{r.reviewer_name || "(isimsiz)"}</b><Stars value={r.rating} /></div>
        <div className="flex items-center gap-1.5 shrink-0">
          {r.overdue && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-300" data-testid="overdue-badge"><Clock className="w-3 h-3" />cevap yok</span>}
          {r.maybe_removed && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 border border-sky-300" data-testid="maybe-removed-badge"><AlertTriangle className="w-3 h-3" />kaldırılmış olabilir</span>}
          {r.dispute_status ? <DisputeBadge status={r.dispute_status} count={r.dispute_count} /> : <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-300">İtiraz edilmedi</span>}
        </div>
      </div>
      <div className="text-xs text-slate-500 mt-1">{fmtDate(r.review_date)}{r.product_name ? ` · ${r.product_name}` : ""}{r.garson ? ` · ${r.garson}` : ""}{r.last_submitted_at ? ` · son itiraz ${fmtDate(r.last_submitted_at.slice(0, 10))}` : ""}</div>
      {r.body && <div className="text-xs text-slate-300 mt-1 line-clamp-1">"{r.body}"</div>}
    </button>
  );
}

export default function DisputesTab() {
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("open");
  const [source, setSource] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    disputesApi.stats().then(setStats).catch(() => {});
    try { setItems(await disputesApi.list({ status, ...(source ? { source } : {}), ...(q.trim() ? { q: q.trim() } : {}) })); } catch {}
    setLoading(false);
  }, [status, source, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="disputes-tab">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
        <Kpi label="Açık itiraz" value={stats && stats.pending + stats.rejected} onClick={() => setStatus("open")} testId="kpi-open" />
        <Kpi label="Cevap bekliyor" value={stats?.pending} cls="text-amber-600" onClick={() => setStatus("pending")} testId="kpi-pending" />
        <Kpi label="Gecikmiş (7+ gün)" value={stats?.overdue} cls="text-orange-600" onClick={() => setStatus("overdue")} testId="kpi-overdue" />
        <Kpi label="Kaldırıldı (bu yıl)" value={stats?.removed_year} cls="text-emerald-600" onClick={() => setStatus("removed")} testId="kpi-removed" />
        <Kpi label="Başarı oranı" value={stats?.success_rate != null ? `%${stats.success_rate}` : "—"} testId="kpi-success" />
        <Kpi label="İtiraz edilmemiş kötü yorum (90 gün)" value={stats?.new_bad} cls="text-indigo-600" onClick={() => setStatus("new")} testId="kpi-new" />
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => { setStatus(k); setSel(null); }} data-testid={`dispute-filter-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${status === k ? "bg-slate-900 text-white border-slate-900" : "bg-slate-800 text-slate-300 border-slate-700"}`}>
            {l}{k === "maybe_removed" && stats?.maybe_removed ? ` (${stats.maybe_removed})` : ""}
          </button>
        ))}
        <select value={source} onChange={e => setSource(e.target.value)} className={inputCls} data-testid="dispute-filter-source">
          <option value="">Tüm kaynaklar</option>
          {Object.entries(SOURCE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Ara…" className={`${inputCls} pl-8`} data-testid="dispute-search" />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_480px] gap-4 items-start">
        <div className={`space-y-2 transition-opacity ${loading && items.length ? "opacity-50" : ""}`} data-testid="dispute-list">
          {loading && items.length === 0 ? <div className="py-10 text-center text-sm text-slate-500">Yükleniyor…</div>
            : items.length === 0 ? <div className="py-10 text-center text-sm text-slate-500 border border-dashed border-slate-700 rounded-xl" data-testid="dispute-empty">Bu filtrede kayıt yok</div>
            : items.map(r => <Row key={r.id} r={r} active={sel === r.id} onClick={() => setSel(r.id)} />)}
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl lg:sticky lg:top-4" data-testid="dispute-detail">
          {sel ? <DisputePanel reviewId={sel} onChanged={load} /> : <div className="p-8 text-sm text-slate-500 text-center">Soldan bir yorum seçin — itiraz geçmişi, görüşmeler ve platform cevapları burada görünür.</div>}
        </div>
      </div>
    </div>
  );
}
